import type { WorkProduct } from "../types/index";

export interface WorkProductRestoreInput {
    previousView: string;
    newView: string;
    /** `selectedWorkProduct` as it stands right before this view change is applied. */
    currentSelectedWorkProduct: WorkProduct | null;
    /** Last work product stashed by a previous editor->features transition, or null. */
    stashedWorkProduct: WorkProduct | null;
}

export interface WorkProductRestoreResult {
    /** Next value for `selectedWorkProduct`. */
    nextSelectedWorkProduct: WorkProduct | null;
    /** Next value to keep in the stash ref. */
    nextStash: WorkProduct | null;
}

/**
 * Computes how `selectedWorkProduct` (and the "last known work product" stash)
 * should change when the app's view transitions from `previousView` to `newView`.
 *
 * Why this exists: leaving the editor for Feature Studio nulls the on-screen
 * work product (Feature Studio drives its own hierarchy-based selection). That
 * null-out is fine for manual "back" navigation, but it breaks agent-chip
 * navigation (`AIChatPanel`'s `handleNavigateToSection`) — a chip clicked from
 * Feature Studio (or any non-editor view) needs to flip the view back to the
 * editor for the *same* work product the section belongs to, and there's
 * nothing else tracking which one that was once `selectedWorkProduct` is null.
 *
 * Rules:
 * - editor -> features: stash the outgoing work product, null the selection
 *   (existing behavior, preserved).
 * - -> editor, with nothing currently selected: restore from the stash. An
 *   explicit selection made just before this call (e.g.
 *   `handleWorkProductSelect` setting `selectedWorkProduct` then switching to
 *   "editor") always wins — this branch only fires when the incoming value is
 *   null.
 * - anything else: pass both values through unchanged.
 *
 * Callers that hard-reset navigation state outside of a view-change flow
 * (switching projects, "back to products") should clear the stash directly
 * instead of routing through this function, so a stale work product from an
 * unrelated project is never resurrected.
 */
export function resolveSelectedWorkProductOnViewChange(
    input: WorkProductRestoreInput
): WorkProductRestoreResult {
    const { previousView, newView, currentSelectedWorkProduct, stashedWorkProduct } = input;

    if (previousView === "editor" && newView === "features") {
        return { nextSelectedWorkProduct: null, nextStash: currentSelectedWorkProduct };
    }

    if (newView === "editor" && !currentSelectedWorkProduct) {
        return { nextSelectedWorkProduct: stashedWorkProduct, nextStash: stashedWorkProduct };
    }

    return { nextSelectedWorkProduct: currentSelectedWorkProduct, nextStash: stashedWorkProduct };
}
